// Copyright (c) 2026 SpicyFriends
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from '@web/src/lib/intl/react'
import { MapIcon } from 'lucide-react'
import type { ComponentProps } from 'react'
import { forwardRef } from 'react'
import DismissHelpHotSpot from 'src/components/primatives/dismiss-help-hot-spot/dismiss-help-hot-spot'

const PLACE_MAP_VIEW_HELP_TYPE = 'placeMapView'

export type PlaceMapViewButtonProps = ComponentProps<typeof Button> & {
  tooltipContent?: string
  showHotSpotIndicator?: boolean
}

const PlaceMapViewButton = forwardRef<HTMLButtonElement, PlaceMapViewButtonProps>(
  ({ tooltipContent, showHotSpotIndicator = true, className, ...props }, forwardedRef) => {
    const { t } = useTranslation()
    const navigate = useNavigate()

    const handleClick = () => {
      navigate({ to: '/place/map' })
    }

    return (
      <Tooltip>
        <TooltipTrigger render={<span className="inline-flex" />}>
          <DismissHelpHotSpot
            helpType={PLACE_MAP_VIEW_HELP_TYPE}
            hotSpotColor="var(--primary-foreground)"
            hotSpotFloatProps={{ className: 'right-1 bottom-7' }}
            showHotSpotIndicator={showHotSpotIndicator}
          >
            <Button
              aria-label={t('map view')}
              className={cn('shadow-xs', className)}
              onClick={handleClick}
              ref={forwardedRef}
              size="icon"
              type="button"
              variant="default"
              {...props}
            >
              <MapIcon />
            </Button>
          </DismissHelpHotSpot>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltipContent ?? t('Show Map View...')}</TooltipContent>
      </Tooltip>
    )
  }
)

PlaceMapViewButton.displayName = 'PlaceMapViewButton'

export default PlaceMapViewButton
